import {
  IRiskGrading,
  IRiskGradingScore,
  ITransactionProfile,
  ITransactionProfileLimit,
} from 'src/app/admin/assisted-ekyc/configuration/configuration.model';

/**
 * Seed configuration for the showcase. Specified in contracts-1 §10.
 *
 * The seven risk-grading parameters are the ones the SRS §M7 names:
 * customer type, occupation, geography, product, expected transaction volume, delivery channel,
 * and PEP / influential-person status.
 *
 * Numbers here are illustrative. The real weights, scores and band thresholds are an open NCC
 * decision (contracts-1 §13.2 item 2) and are meant to be edited through the Configuration pages.
 */

/**
 * The grading sheet: nine questions grouped under six Risk Grading Types.
 * `categoryName` is the group heading; `description` is the question itself.
 */
export const RISK_GRADING_SEED: IRiskGrading[] = [
  { code: 'RG-001', categoryName: 'Type of On-boarding', description: 'What is the type of On-boarding?', position: 1, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
  { code: 'RG-002', categoryName: 'Geographic Risks', description: 'What is Client Status', position: 2, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
  { code: 'RG-003', categoryName: 'Geographic Risks', description: 'Is the client from a country that is in the watchlist?', position: 3, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
  { code: 'RG-004', categoryName: 'Type of Customer', description: 'Is the client a Politically Exposed Person (PEP) or a high official of an international organization?', position: 4, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
  { code: 'RG-005', categoryName: 'Type of Customer', description: 'Is the client a family member or close associate of a Politically Exposed Person (PEP)?', position: 5, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
  { code: 'RG-006', categoryName: 'Type of Customer', description: 'Is the client an influential person?', position: 6, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
  { code: 'RG-007', categoryName: 'Product and Channel Risk', description: 'What is the type of Product?', position: 7, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
  { code: 'RG-008', categoryName: 'Transactional Risks', description: 'How much is the client average yearly transactions worth?', position: 8, status: true, riskGradingScoreType: 'RANGE', weight: 1 },
  { code: 'RG-009', categoryName: 'Transparency Risk', description: 'Has the client provided a credible source of funds?', position: 9, status: true, riskGradingScoreType: 'GENERAL', weight: 1 },
];

/** First option of each question is the default the sheet opens with. */
export const RISK_GRADING_SCORE_SEED: IRiskGradingScore[] = [
  // RG-001 Type of On-boarding
  { code: 'RGS-101', riskGradingCode: 'RG-001', description: 'Assisted — branch officer, customer present', booleanValue: false, score: 2 },
  { code: 'RGS-102', riskGradingCode: 'RG-001', description: 'Self-service — digital channel', booleanValue: false, score: 4 },
  { code: 'RGS-103', riskGradingCode: 'RG-001', description: 'Third-party / agent introduced', booleanValue: false, score: 6 },

  // RG-002 Client status
  { code: 'RGS-201', riskGradingCode: 'RG-002', description: 'Resident Bangladeshi', booleanValue: false, score: 1 },
  { code: 'RGS-202', riskGradingCode: 'RG-002', description: 'Non-resident Bangladeshi (NRB)', booleanValue: false, score: 3 },
  { code: 'RGS-203', riskGradingCode: 'RG-002', description: 'Foreign national', booleanValue: false, score: 5 },

  // RG-003 Watchlist country
  { code: 'RGS-301', riskGradingCode: 'RG-003', description: 'No', booleanValue: false, score: 0 },
  { code: 'RGS-302', riskGradingCode: 'RG-003', description: 'Yes', booleanValue: false, score: 10 },

  // RG-004 PEP
  { code: 'RGS-401', riskGradingCode: 'RG-004', description: 'No', booleanValue: false, score: 0 },
  { code: 'RGS-402', riskGradingCode: 'RG-004', description: 'Yes', booleanValue: false, score: 10 },

  // RG-005 PEP family member or close associate
  { code: 'RGS-501', riskGradingCode: 'RG-005', description: 'No', booleanValue: false, score: 0 },
  { code: 'RGS-502', riskGradingCode: 'RG-005', description: 'Yes', booleanValue: false, score: 8 },

  // RG-006 Influential person
  { code: 'RGS-601', riskGradingCode: 'RG-006', description: 'Yes — influential person', booleanValue: false, score: 1 },
  { code: 'RGS-602', riskGradingCode: 'RG-006', description: 'No', booleanValue: false, score: 0 },

  // RG-007 Product type
  { code: 'RGS-701', riskGradingCode: 'RG-007', description: 'Savings account', booleanValue: false, score: 1 },
  { code: 'RGS-702', riskGradingCode: 'RG-007', description: 'Current account', booleanValue: false, score: 3 },
  { code: 'RGS-703', riskGradingCode: 'RG-007', description: 'Foreign currency account', booleanValue: false, score: 5 },

  // RG-008 Average yearly transaction value
  { code: 'RGS-801', riskGradingCode: 'RG-008', description: 'Up to BDT 1,000,000', booleanValue: false, score: 2, fromAmount: 0, toAmount: 1000000 },
  { code: 'RGS-802', riskGradingCode: 'RG-008', description: 'BDT 1,000,001 – 5,000,000', booleanValue: false, score: 4, fromAmount: 1000001, toAmount: 5000000 },
  { code: 'RGS-803', riskGradingCode: 'RG-008', description: 'Above BDT 5,000,000', booleanValue: false, score: 6, fromAmount: 5000001, toAmount: 999999999 },

  // RG-009 Credible source of funds
  { code: 'RGS-901', riskGradingCode: 'RG-009', description: 'Yes — documented and credible', booleanValue: false, score: 5 },
  { code: 'RGS-902', riskGradingCode: 'RG-009', description: 'Partially documented', booleanValue: false, score: 8 },
  { code: 'RGS-903', riskGradingCode: 'RG-009', description: 'No', booleanValue: false, score: 12 },
];


export const TRANSACTION_PROFILE_SEED: ITransactionProfile[] = [
  {
    transactionProfileCode: 'TP-001',
    description: 'Simplified eKYC — monthly turnover up to BDT 100,000',
    status: true,
    transactionProfileSlab: {
      transactionProfileSlabCode: 'TPS-001',
      description: 'Up to BDT 100,000',
      ekycType: 'SIMPLIFIED',
      startRange: '0.01',
      endRange: '100000.00',
    },
  },
  {
    transactionProfileCode: 'TP-002',
    description: 'Regular KYC — monthly turnover above BDT 100,000',
    status: true,
    transactionProfileSlab: {
      transactionProfileSlabCode: 'TPS-002',
      description: 'More than BDT 100,000',
      ekycType: 'REGULAR',
      startRange: '100000.01',
      endRange: '10000000.00',
    },
  },
  {
    transactionProfileCode: 'TP-003',
    description: 'Regular KYC — high turnover (retired profile)',
    status: false,
    transactionProfileSlab: {
      transactionProfileSlabCode: 'TPS-003',
      description: 'Above BDT 10,000,000',
      ekycType: 'REGULAR',
      startRange: '10000000.01',
      endRange: '99999999.00',
    },
  },
];

export const TRANSACTION_PROFILE_LIMIT_SEED: ITransactionProfileLimit[] = [
  {
    transactionProfileLimitCode: 'TPL-001',
    transactionProfileCode: 'TP-001',
    description: 'Simplified — cash',
    type: 'CASH',
    mandatory: true,
    monthlyTotalCreditTransactionCount: 10,
    monthlyTotalCreditTransactionAmount: 100000,
    maxCreditAmountPerTransaction: 50000,
    monthlyTotalDebitTransactionCount: 10,
    monthlyTotalDebitTransactionAmount: 100000,
    maxDebitAmountPerTransaction: 50000,
    status: true,
  },
  {
    transactionProfileLimitCode: 'TPL-002',
    transactionProfileCode: 'TP-001',
    description: 'Simplified — transfer instrument',
    type: 'TRANSFER_INSTRUMENT',
    mandatory: false,
    monthlyTotalCreditTransactionCount: 8,
    monthlyTotalCreditTransactionAmount: 80000,
    maxCreditAmountPerTransaction: 40000,
    monthlyTotalDebitTransactionCount: 8,
    monthlyTotalDebitTransactionAmount: 80000,
    maxDebitAmountPerTransaction: 40000,
    status: true,
  },
  {
    transactionProfileLimitCode: 'TPL-003',
    transactionProfileCode: 'TP-002',
    description: 'Regular — cash',
    type: 'CASH',
    mandatory: true,
    monthlyTotalCreditTransactionCount: 40,
    monthlyTotalCreditTransactionAmount: 2500000,
    maxCreditAmountPerTransaction: 500000,
    monthlyTotalDebitTransactionCount: 40,
    monthlyTotalDebitTransactionAmount: 2500000,
    maxDebitAmountPerTransaction: 500000,
    status: true,
  },
  {
    transactionProfileLimitCode: 'TPL-004',
    transactionProfileCode: 'TP-002',
    description: 'Regular — transfer instrument',
    type: 'TRANSFER_INSTRUMENT',
    mandatory: true,
    monthlyTotalCreditTransactionCount: 60,
    monthlyTotalCreditTransactionAmount: 5000000,
    maxCreditAmountPerTransaction: 1000000,
    monthlyTotalDebitTransactionCount: 60,
    monthlyTotalDebitTransactionAmount: 5000000,
    maxDebitAmountPerTransaction: 1000000,
    status: true,
  },
  {
    transactionProfileLimitCode: 'TPL-005',
    transactionProfileCode: 'TP-002',
    description: 'Regular — foreign remittance',
    type: 'FOREIGN_REMITTANCE',
    mandatory: false,
    monthlyTotalCreditTransactionCount: 6,
    monthlyTotalCreditTransactionAmount: 1500000,
    maxCreditAmountPerTransaction: 500000,
    monthlyTotalDebitTransactionCount: 0,
    monthlyTotalDebitTransactionAmount: 0,
    maxDebitAmountPerTransaction: 0,
    status: true,
  },
];

/**
 * Risk bands.
 *
 * NOTE: none of the four Configuration pages covers band thresholds — the reference hardcodes
 * `totalScore < 15 ? 'LOW' : 'HIGH'` directly in a template (contracts-1 §10 note). Bands live
 * here so the grading engine reads configuration rather than a magic number, but a Risk Band
 * admin page is a gap worth raising with NCC (contracts-1 §13.2 item 2).
 */
export interface IRiskBand {
  band: 'LOW' | 'MEDIUM' | 'HIGH';
  minScore: number;
  maxScore: number;
  severity: 'success' | 'warning' | 'danger';
}

export const RISK_BAND_SEED: IRiskBand[] = [
  { band: 'LOW', minScore: 0, maxScore: 10, severity: 'success' },
  { band: 'MEDIUM', minScore: 11, maxScore: 17, severity: 'warning' },
  { band: 'HIGH', minScore: 18, maxScore: 9999, severity: 'danger' },
];

export const RISK_MODEL_VERSION = 'RM-2026.1';
